import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const FloatingElements = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is 'md' in Tailwind
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (isMobile) {
    return (
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none">
        {/* On mobile, we only keep the very subtle static elements */}
        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03]">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="network-pattern-mobile" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#network-pattern-mobile)" className="text-gray-400 dark:text-gray-600" />
          </svg>
        </div>
        <div className="absolute top-[-10%] right-[-10%] w-[300px] h-[300px] rounded-full blur-[80px] opacity-[0.02] dark:opacity-[0.04]" 
             style={{ background: 'radial-gradient(circle, var(--accent-green) 0%, transparent 70%)' }} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none">
      {/* 1. Subtle Network Nodes (The "Network" Metaphor) */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="network-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="currentColor" />
              <circle cx="98" cy="98" r="1" fill="currentColor" />
              <line x1="2" y1="2" x2="98" y2="98" stroke="currentColor" strokeWidth="0.5" strokeDasharray="4 4" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#network-pattern)" className="text-gray-400 dark:text-gray-600" />
        </svg>
      </div>

      {/* 2. Floating Abstract Shapes (Hexagons/Tech Fragment) */}
      <motion.div
        animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{ willChange: "transform, opacity" }}
        className="absolute top-[10%] left-[5%] opacity-[0.02] dark:opacity-[0.04]"
      >
        <svg width="200" height="200" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50 5L93.3 30V80L50 105L6.7 80V30L50 5Z" stroke="currentColor" strokeWidth="1" className="text-gray-500" />
        </svg>
      </motion.div>

      <motion.div
        animate={{
          y: [0, 20, 0],
          rotate: [0, -5, 0],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{ willChange: "transform, opacity" }}
        className="absolute bottom-[15%] right-[8%] opacity-[0.02] dark:opacity-[0.04]"
      >
        <svg width="300" height="300" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50 5L93.3 30V80L50 105L6.7 80V30L50 5Z" stroke="currentColor" strokeWidth="0.5" className="text-[#3f782b]" />
        </svg>
      </motion.div>

      {/* 3. Pulsing Circuit Lines (The "Electric" Metaphor) */}
      <div className="absolute top-1/4 right-0 w-64 h-64 opacity-[0.015] dark:opacity-[0.03]">
        <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <motion.path
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            style={{ willChange: "opacity" }}
            d="M0 20H40V60H100"
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-gray-400 dark:text-gray-500"
          />
          <motion.path
            animate={{ opacity: [0.1, 0.6, 0.1] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            style={{ willChange: "opacity" }}
            d="M0 80H20V40H60V0"
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-gray-400 dark:text-gray-500"
          />
        </svg>
      </div>

      {/* Ambient Glowing Orbs (Very Subdued) - Simplified for performance */}
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full blur-[120px] opacity-[0.02] dark:opacity-[0.04]" 
           style={{ background: 'var(--accent-green)' }} />
      
      <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full blur-[150px] opacity-[0.015] dark:opacity-[0.03]" 
           style={{ background: 'rgba(22, 88, 131, 1)' }} />

      {/* Static Pattern Overlay (Replaces heavy noise filter) */}
      <div className="absolute inset-0 opacity-[0.01] pointer-events-none mix-blend-overlay"
           style={{ backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`, backgroundSize: '32px 32px' }} />
    </div>
  );
};

export default FloatingElements;

